import React from 'react';

import { styled } from '../../../stitches.config';

import { Button } from '../Button/Button';

export type PaginationProps = {
  currentPage: number;
  itemCount: number;
  pageSize: number;
  onChange: (nextPage: number) => void;
  scrollTo?: Window | HTMLElement;
};

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const InputContainer = styled('div', {
  display: 'flex',
  alignContent: 'space-between',
  gap: '$3',
  alignItems: 'center',
  padding: '$3',
  fontSize: '$2',
});

const Input = styled('input', {
  'all': 'unset',
  'border': '1px solid $slate6',
  'borderRadius': '$small',
  'background': '$slate1',
  'padding': '$3 $5',
  'textAlign': 'center',
  '&:focus': {
    outline: 'none',
  },
});

export const Pagination = ({ currentPage, itemCount, pageSize, onChange, scrollTo }: PaginationProps) => {
  const pageCount = Math.ceil(itemCount / pageSize);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pageCount;

  const changeAndScroll = (page: number) => {
    if (page < 1 || page > pageCount) {
      return;
    }
    onChange(page);
    if (scrollTo) {
      scrollTo.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = Number(e.currentTarget.value);
      if (value >= 1 && value <= pageCount) {
        changeAndScroll(value);
      }
    }
  };

  return (
    <Container>
      <Button
        disabled={isFirstPage}
        onClick={() => changeAndScroll(1)}
        icon="first_page"
        weight="minimal"
        size="small"
      />
      <Button
        disabled={isFirstPage}
        onClick={() => changeAndScroll(currentPage - 1)}
        icon="chevron_left"
        weight="minimal"
        size="small"
      />
      <InputContainer>
        <Input
          key={currentPage}
          defaultValue={currentPage}
          onBlur={e => changeAndScroll(Number(e.target.value))}
          min={1}
          max={pageCount}
          onKeyDown={handleKeyDown}
          disabled={pageCount < 2}
          size={String(currentPage).length}
        />
        of {pageCount}
      </InputContainer>
      <Button
        disabled={isLastPage}
        onClick={() => changeAndScroll(currentPage + 1)}
        rightIcon="chevron_right"
        weight="minimal"
        size="small"
      />
      <Button
        disabled={isLastPage}
        onClick={() => changeAndScroll(pageCount)}
        rightIcon="last_page"
        weight="minimal"
        size="small"
      />
    </Container>
  );
};
